import {
  useState,
  useMemo,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Modal,
  Typography,
  Box,
  Button,
  FormControl,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
  Grid,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { useStyles } from "../../Styles/styles";
import { useLazyQuery, useMutation } from "@apollo/client";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { handleUuid, wordSort } from "../../utils/utils";
import { SetupContext } from "../../Contexts/SetupContext";
import _ from "lodash";
import { ADD_AUDIT_LOG } from "../../Mutations/AddAuditLog";
import { createEmptyVis } from "../../utils/EmptyObjects";
import HelpIconButton from "../HelpIconButton";
import { throttleFunction } from "../../utils/throttleFunction";
import { registrationParser } from "./RegistrationParser";
import { ADD_OR_UPDATE_VEHICLE } from "./Mutations/AddOrUpdateVehicle";
import { ADD_OR_UPDATE_VEHICLE_IN_INCIDENT } from "./Mutations/AddOrUpdateVehicleInIncident";
import { VEHICLE_SEARCH } from "./Queries/VehicleSearch";
import { DashboardContext } from "../../Contexts/DashboardContext";

import CustomAutoComplete from "../CustomAutoComplete";
import { VIN_DECODE_RESULT } from "../../Queries/VinDecodeResult";
import TopHalf from "../DMVQueryModal/TopHalf";
import {
  handleChange,
  handleMutationCompleted,
  handleRunQuery,
  initializeFormState,
  provinces,
  isQueryFormValid,
  variableFields,
} from "../DMVQueryModal/dmvQueryModalHandlers";
import BottomHalf, {
  propInSights,
  reasonCodes,
} from "../DMVQueryModal/BottomHalf";
import { vehicleQueryTypes } from "../DMVQueryModal/queryTypes";
import { SEND_QUERY_REQUEST } from "../../Mutations/SendQueryRequest";
import { SET_STATUS } from "../../Mutations/SetStatus";
import { returnMakeCode } from "./makeCodes";
import ScanInput from "../ScanInput/ScanInput";
import { UserContext } from "../../Contexts/UserContext";

import moment from "moment";

export const returnEmptyVis = viss => {
  let emptyVis = viss?.find(
    vis =>
      !vis?.vehicle?.vehicleMake &&
      !vis?.vehicle?.vehiclePlate &&
      !vis?.vehicle?.vehicleVin,
  );
  if (emptyVis) {
    return emptyVis;
  } else {
    return createEmptyVis();
  }
};
export const convertScanToVehicle = async (
  scanObj,
  vinDecodeMutation,
  online,
) => {
  let res;
  if (online && scanObj?.IR?.vin) {
    res = await vinDecodeMutation({ variables: { vin: scanObj?.IR?.vin } });
  }
  const { vehicleMake, vehicleModel } = res?.data?.vinDecodeResult || {};
  const registrantInformation = {};
  registrantInformation["name"] = scanObj?.IR?.registrantName;
  registrantInformation["street"] = scanObj?.IR?.physicalAddressStreet;
  registrantInformation["city"] = scanObj?.IR?.physicalAddressCity;
  registrantInformation["state"] = scanObj?.IR?.physicalAddressJurisdiction;
  registrantInformation["zip"] = scanObj?.IR?.physicalAddressZip?.slice(0, 5);
  registrantInformation["usdot"] = scanObj?.MC?.carrierUsDotNumber;
  registrantInformation["carrier"] = scanObj?.MC?.carrierName;
  registrantInformation["registrationIssueDate"] =
    scanObj?.IR?.registrationIssueDate;
  registrantInformation["registrationExpirationDate"] =
    scanObj?.IR?.registrationExpirationDate;

  const motorCarrierInformation = {};
  motorCarrierInformation["name"] = scanObj?.MC?.carrierName;
  motorCarrierInformation["street"] = scanObj?.MC?.mailingAddressStreet;
  motorCarrierInformation["city"] = scanObj?.MC?.mailingAddressCity;
  motorCarrierInformation["state"] = scanObj?.MC?.mailingAddressJurisdiction;
  motorCarrierInformation["zip"] = scanObj?.MC?.mailingAddressZip?.slice(0, 5);
  motorCarrierInformation["usdot"] = scanObj?.MC?.carrierUsDotNumber;

  const vehicleInformation = {};
  vehicleInformation["unit"] = scanObj?.IR?.unitNumber;
  vehicleInformation["plateNumber"] = scanObj?.IR?.plateNumber;
  vehicleInformation["vin"] = scanObj?.IR?.vin;
  vehicleInformation["vehicleType"] = scanObj?.IR?.vehicleType;
  vehicleInformation["vehicleMakeName"] = scanObj?.IR?.vehicleMake;
  vehicleInformation["vehicleMake"] = vehicleMake;
  vehicleInformation["vehicleModel"] = vehicleModel;
  vehicleInformation["modelYear"] = scanObj?.IR?.modelYear;
  vehicleInformation["owner"] = scanObj?.IR?.registrantName;
  vehicleInformation["grossWeight"] = scanObj?.IR?.grossVehicleWeight;
  vehicleInformation["baseWeight"] = scanObj?.IR?.baseStateRegisteredWeight;
  vehicleInformation["seatsAxles"] = scanObj?.IR?.seatsAxles;
  vehicleInformation["state"] = scanObj?.IR?.physicalAddressJurisdiction;

  const weightChart = scanObj?.RW;

  return {
    registrantInformation,
    motorCarrierInformation,
    vehicleInformation,
    weightChart,
  };
};
const RegistrationScanModal = ({
  modalOpen,
  setModalOpen,
  user,
  incidentId,
  incidentIndex,
  incident,
  nightMode,
  onlineState,
  setPopper,
}) => {
  const classes = useStyles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedClasses = useMemo(() => classes, []);

  const { setup, vehicleQueryCode, personQueryCode } = useContext(SetupContext);
  const { tokenDisabled } = useContext(UserContext);
  const {
    setupLists: { states, licenseTypes },
  } = setup || {};
  const { incidentDispatch } = useContext(IncidentsContext);
  const { ADD_OR_UPDATE_VIS } = INCIDENT_ACTIONS;

  let emptyVis = returnEmptyVis(incident.vehicleIncidentSpecifics);

  const [data, setData] = useState("");
  const [vehicleObj, setVehicleObj] = useState(null);
  const [registrant, setRegistrant] = useState(null);
  const [motorCarrier, setMotorCarrier] = useState(null);
  const [weightChart, setWeightChart] = useState(null);
  const [showResults, setShowResults] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mergeChoice, setMergeChoice] = useState({});
  const [wait, setWait] = useState(false);

  const [isParsing, setIsParsing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [parsingError, setParsingError] = useState(null);

  const [expandUserInfo, setExpandUserInfo] = useState(false);

  const [formState, setFormState] = useState({});
  const [querySelection, setQuerySelection] = useState(vehicleQueryTypes[0]);
  const [queryState, setQueryState] = useState({
    year: new Date().getFullYear().toString(),
    licenseType:
      licenseTypes?.find(type => type?.name?.startsWith("PC")) ??
      licenseTypes?.[0],
  });
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    const returnFormStateFromPersonVehicle = (vehicle, person, fromScan) => {
      const findState = fromScan ? person?.state : vehicle?.vehicleState?.name;
      let locations = querySelection?.shortcode === "XQ" ? provinces : states;
      const location = locations.find(s => s?.name === findState);
      return { destination: location || formState["destination"] };
    };

    const returnQueryStateFromPersonVehicle = (
      vehicle,
      fromScan,
    ) => {
      return {
        licensePlateNumber: fromScan
          ? vehicle?.plateNumber
          : vehicle?.vehiclePlate?.name,
        vin: fromScan ? vehicle?.vin : vehicle?.vehicleVin?.name,
        vehicleYear: fromScan ? vehicle?.modelYear : vehicle?.year,
        make: returnMakeCode(vehicle?.vehicleMake?.name),
        vehicleModel: vehicle?.vehicleModel?.name,
        reasonCode:
          reasonCodes.find(r => r?.name?.startsWith("T")) ||
          queryState?.reasonCode,
        propInSight:
          propInSights.find(r => r?.name?.startsWith("Y")) ||
          queryState?.propInSight,
      };
    };

    let fromScan;
    let vehicleToUse;

    if (selectedVehicle?.id && vehicleObj) {
      fromScan = false;
      vehicleToUse = selectedVehicle;
    }

    if (!selectedVehicle && vehicleObj && registrant) {
      fromScan = true;
      vehicleToUse = vehicleObj;
    }

    if (fromScan !== undefined && vehicleToUse !== undefined) {
      const newFormState = returnFormStateFromPersonVehicle(
        vehicleToUse,
        registrant,
        fromScan,
      );
      const newQueryState = returnQueryStateFromPersonVehicle(
        vehicleToUse,
        fromScan,
        newFormState?.province,
      );
      let keyCorrectMergeChoices = { ...mergeChoice };
      if (keyCorrectMergeChoices["year"]) {
        keyCorrectMergeChoices["vehicleYear"] = keyCorrectMergeChoices["year"];
        delete keyCorrectMergeChoices["year"];
      }
      if (keyCorrectMergeChoices["make"]) {
        keyCorrectMergeChoices["make"] = returnMakeCode(
          keyCorrectMergeChoices["make"]?.name,
        );
      }
      let mergedQueryState = { ...newQueryState, ...keyCorrectMergeChoices };
      setQueryState(prevState => ({
        ...prevState,
        ...mergedQueryState,
      }));

      setFormState(prevState => ({
        ...prevState,
        ...newFormState,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVehicle,
    vehicleObj,
    registrant,
    licenseTypes,
    states,
    mergeChoice,
    provinces,
  ]);

  useEffect(() => {
    if (user?.officer?.deptAttribute) {
      initializeFormState(user, states, setFormState, setExpandUserInfo);
    }
  }, [states, user]);

  const dashRef = useRef(null);

  const [decodeVin] = useLazyQuery(VIN_DECODE_RESULT, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  const [addOrUpdateVehicleInIncident] = useMutation(
    ADD_OR_UPDATE_VEHICLE_IN_INCIDENT,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: data => {
        if (data) {
          incidentDispatch({
            type: ADD_OR_UPDATE_VIS,
            payload: {
              incidentId: incidentId,
              incidentIndex: incidentIndex,
              visUuid: data[Object.keys(data)[0]].vehicleIncidentSpecificUuid,
              vis: data[Object.keys(data)[0]].vehicleIncidentSpecific,
            },
          });
          setPopper({
            open: true,
            content: "Vehicle added to the incident.",
            severity: "success",
          });
        }
      },
    },
  );

  const [addOrUpdateVehicle] = useMutation(ADD_OR_UPDATE_VEHICLE, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (data) {
        setPopper({
          open: true,
          content: "Vehicle created.",
          severity: "success",
        });
      }
    },
  });

  const throttledAddOrUpdateVehicle = throttleFunction(addOrUpdateVehicle);

  const [addAuditLog] = useMutation(ADD_AUDIT_LOG);

  const handleSubmit = useCallback(
    async event => {
      event?.preventDefault();
      setIsParsing(true);
      setSubmitted(true);
      let parsedResult = null;
      let validScan = false;
      try {
        parsedResult = registrationParser(data.toString());
        const {
          registrantInformation,
          motorCarrierInformation,
          vehicleInformation,
          weightChart,
        } = await convertScanToVehicle(parsedResult, decodeVin, onlineState);
        setVehicleObj(vehicleInformation);
        setRegistrant(registrantInformation);
        setMotorCarrier(motorCarrierInformation);
        setWeightChart(weightChart);

        let state = states.find(s => s?.name === registrantInformation?.state);
        let province = provinces.find(
          s => s?.name === registrantInformation?.state,
        );
        let query = vehicleQueryTypes.find(q => q.shortcode === "SVQ");

        if (state || province) {
          if (province) {
            query = vehicleQueryTypes.find(q => q.shortcode === "XQ");
          }
          setQuerySelection(query);
        }

        validScan = isVehicleValid(vehicleInformation);
        if (!validScan) setParsingError(true);
      } catch (error) {
        setParsingError(true);
        console.error(error);
      }

      onlineState &&
        addAuditLog({
          variables: {
            input: {
              incidentId: incidentId,
              originatorId: user?.id,
              message: data,
              action: validScan
                ? "Successful Registration Scan"
                : "Failed Registration Scan",
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      setIsParsing(false);
      return true;
    },
    [onlineState, addAuditLog, incidentId, user?.id, data, decodeVin, states],
  );

  const handleAddNewVehicle = async () => {
    let input = {
      id: emptyVis?.vehicle?.id,
      originatorId: user?.id,
      incidentId: incidentId ? incidentId : null,
      vehicleMakeId: vehicleObj?.vehicleMake?.id,
      vehicleModelId: vehicleObj?.vehicleModel?.id,
      vehicleVinName: vehicleObj?.vin,
      vehiclePlateName: vehicleObj?.plateNumber,
      vehiclePlateId: handleUuid(),
      vehicleVinId: handleUuid(),
      year: vehicleObj?.modelYear,
      vehicleStateId: states.find(s => s?.name?.startsWith(registrant?.state))
        ?.id,
    };
    if (onlineState) {
      const newVehicleRes = await throttledAddOrUpdateVehicle({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      addOrUpdateVehicleInIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            vehicleId: newVehicleRes?.data?.addOrUpdateVehicle?.vehicle?.id,
            vehicleIncidentSpecificId: emptyVis?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    } else {
      throttledAddOrUpdateVehicle({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      addOrUpdateVehicleInIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            vehicleId: emptyVis?.vehicle?.id,
            vehicleIncidentSpecificId: emptyVis?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      let visClone = _.cloneDeep(emptyVis) || {};
      visClone.vehicle = {
        vehicleMake: vehicleObj?.vehicleMake,
        vehicleModel: vehicleObj?.vehicleModel,
        vehiclePlate: {
          id: input?.vehiclePlateId,
          name: input?.vehiclePlateName,
        },
        vehicleVin: {
          id: input?.vehicleVinId,
          name: input?.vehicleVinName,
        },
        year: input?.modelYear,
        vehicleState: states.find(s => s?.name?.startsWith(registrant?.state)),
      };
      incidentDispatch({
        type: ADD_OR_UPDATE_VIS,
        payload: {
          incidentId: incidentId,
          incidentIndex: incidentIndex,
          visUuid: visClone.uuid,
          vis: visClone,
        },
      });
    }
    handleSubmitQuery();
    handleClose();
  };

  const handleSelectedAdd = async () => {
    const input = {
      incidentId: incidentId,
      vehicleId: selectedVehicle?.id,
      vehicleIncidentSpecificId: emptyVis?.id,
      originatorId: user?.id,
    };
    let vehicleInput = {
      originatorId: user?.id,
      id: selectedVehicle?.id,
    };
    if (Object.keys(mergeChoice).length > 0) {
      if (mergeChoice["make"]) {
        vehicleInput["vehicleMakeId"] = mergeChoice?.make?.id;
      }
      if (mergeChoice["model"]) {
        vehicleInput["vehicleModelId"] = mergeChoice?.model?.id;
      }
      if (mergeChoice["year"]) {
        vehicleInput["year"] = mergeChoice?.year;
      }
      if (mergeChoice["plate"]) {
        vehicleInput["vehiclePlateName"] = mergeChoice?.plate;
      }
      if (mergeChoice["state"]) {
        vehicleInput["vehicleStateId"] = states.find(s =>
          s?.name?.startsWith(mergeChoice["state"]),
        )?.id;
      }
      if (mergeChoice["vin"]) {
        vehicleInput["vehicleVinName"] = mergeChoice?.vin;
      }
      addOrUpdateVehicle({
        variables: { input: vehicleInput },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
    if (input.vehicleId) {
      addOrUpdateVehicleInIncident({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      handleSubmitQuery();
      handleClose();
    } else {
      alert("No vehicle selected");
    }
    return;
  };

  const [sendQueryRequest] = useMutation(SEND_QUERY_REQUEST, {
    onCompleted: data => handleMutationCompleted(data, setPopper, null, false),
    onError: err => setPopper({ open: true, content: err?.message }),
  });
  const [setStatus] = useMutation(SET_STATUS);

  const checkSwitchAccess = () => {
    if (tokenDisabled) {
      alert(
        "Switch access has been blocked due to the SoftToken requirement being disabled.",
      );
      return false;
    } else {
      return true;
    }
  };

  const handleSubmitQuery = e => {
    const allowQuery = checkSwitchAccess();

    const queryHandlers = {
      formState,
      queryState,
      querySelection,
      user,
      incidentId,
      sendQueryRequest,
      setStatus,
      handleClose: () => {},
      vehicleQueryCode,
      personQueryCode,
    };
    if (allowQuery) {
      handleRunQuery(e, queryHandlers, null, isQueryFormValid, tokenDisabled);
    }
  };

  const handleClose = () => {
    clearInput();
    setModalOpen(false);
  };

  const inputRef = useRef(null);

  const clearInput = () => {
    if (inputRef?.current) {
      inputRef.current.value = "";
    }
    setData("");
    setVehicleObj(null);
    setRegistrant(null);
    setMotorCarrier(null);
    setWeightChart(null);
    setIsParsing(false);
    setSelectedVehicle(null);
    setSubmitted(false);
    setParsingError(null);
  };

  const isVehicleValid = vehicle => {
    const validationObj = {
      plate: vehicle?.plateNumber?.length > 0,
      vin: vehicle?.vin?.length > 0,
    };
    return !Object.keys(validationObj)
      .map(key => validationObj[key])
      .includes(false);
  };

  useEffect(() => {
    if (vehicleObj?.vin && onlineState) {
      decodeVin({ variables: { vin: vehicleObj?.vin } });
    }
  }, [vehicleObj, decodeVin, onlineState]);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  return (
    <Modal
      open={modalOpen}
      onClose={() => handleClose()}
      onKeyDown={e => e.key === "Escape" && handleClose()}
      className={classes.modal}
    >
      <div
        className={classes.paper}
        ref={dashRef}
        style={{ padding: "0px", overflowY: "inherit" }}
      >
        <Accordion
          square
          expanded={showResults}
          onChange={() => setShowResults(!showResults)}
          TransitionProps={{ unmountOnExit: false }}
        >
          <AccordionSummary
            style={{
              backgroundColor: nightMode ? "#616161" : "#fae8ad",
              margin: 0,
            }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: nightMode ? "white" : "#303e4f",
                }}
                className={memoizedClasses.expansionPanelTypography}
              >
                Registration Scan{" "}
                {showResults ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Typography>
              <HelpIconButton objectname="mobile_license_scan" />
            </Box>
          </AccordionSummary>
          <AccordionDetails onClick={e => e.stopPropagation()}>
            <div className={classes.licenseScanModal}>
              <ScanInput
                id={"registration-scan-input"}
                inputRef={inputRef}
                inputDisabled={!!vehicleObj}
                submitDisabled={data === "" || !!vehicleObj || isParsing}
                clearDisabled={data === "" || isParsing}
                submitCallback={handleSubmit}
                submitted={submitted}
                onClearCallback={clearInput}
                keyData={data}
                setKeyData={setData}
                decoding={isParsing}
                error={
                  (!!vehicleObj && !isVehicleValid(vehicleObj)) || parsingError
                }
                incidentId={incidentId}
                user={user}
                wait={wait}
                setWait={setWait}
              />
              <br />

              {vehicleObj && (
                <VehicleScanResult
                  vehicle={vehicleObj}
                  registrant={registrant}
                  motorCarrier={motorCarrier}
                  weights={weightChart}
                  setVehicleObj={setVehicleObj}
                >
                  <div>
                    <TopHalf
                      expandUserInfo={expandUserInfo}
                      setExpandUserInfo={setExpandUserInfo}
                      nightMode={nightMode}
                      formState={formState}
                      handleChange={handleChange}
                      setFormState={setFormState}
                      querySelection={querySelection}
                      setQuerySelection={setQuerySelection}
                      user={user}
                      queryTypes={vehicleQueryTypes}
                    />
                    {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Keep focus on the scan flow when query fields appear in the scan result. */}
                    <BottomHalf autoFocus={false}
                      isRegistrationScan={true}
                      queryState={queryState}
                      setQueryState={setQueryState}
                      queryType={querySelection?.name}
                      licenseTypes={licenseTypes}
                      user={user}
                    >
                      {variableFields(
                        querySelection,
                        formState,
                        setFormState,
                        handleChange,
                        states,
                        classes,
                        inputLabel,
                        labelWidth,
                        user,
                      )}
                    </BottomHalf>
                  </div>
                </VehicleScanResult>
              )}
              <DisplayMatches
                states={states}
                vehicleObj={vehicleObj}
                user={user}
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
                scanDone={!!data}
                onlineState={onlineState}
                mergeChoice={mergeChoice}
                setMergeChoice={setMergeChoice}
              />

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-around"
                style={{ width: "100%" }}
              >
                <Button
                  disabled={
                    !vehicleObj ||
                    !!selectedVehicle ||
                    !isVehicleValid(vehicleObj)
                  }
                  style={{
                    width: "fit-content",
                    marginTop: "1vh",
                    marginBottom: "3vh",
                    marginRight: "2vw",
                  }}
                  variant="contained"
                  onClick={handleAddNewVehicle}
                >
                  Create New Vehicle
                </Button>
                <Button
                  disabled={!selectedVehicle}
                  style={{
                    width: "fit-content",
                    marginTop: "1vh",
                    marginBottom: "3vh",
                    marginRight: "2vw",
                  }}
                  variant="contained"
                  onClick={handleSelectedAdd}
                >
                  Use Existing Vehicle
                </Button>
                <Button
                  style={{
                    width: "fit-content",
                    marginTop: "1vh",
                    marginBottom: "3vh",
                    marginRight: "2vw",
                  }}
                  variant="contained"
                  onClick={handleClose}
                >
                  Close
                </Button>
              </Box>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </Modal>
  );
};

export default RegistrationScanModal;

const VehicleScanResult = ({
  vehicle,
  registrant,
  motorCarrier,
  weights,
  setVehicleObj,
  children,
}) => {
  const { indexedDb } = useContext(DashboardContext);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);

  useEffect(() => {
    indexedDb.vehicle_makes
      .toArray()
      .then(res => {
        setVehicleMakes(wordSort("asc", null, res, "name"));
      })
      .catch(err => console.error(err));
  }, [indexedDb.vehicle_makes]);

  useEffect(() => {
    indexedDb.vehicle_models
      .toArray()
      .then(res => {
        setVehicleModels(wordSort("asc", null, res, "name"));
      })
      .catch(err => console.error(err));
  }, [indexedDb.vehicle_models]);

  function renderObjectAsGrid(weights) {
    const keys = Object.keys(weights);

    return (
      <Grid container spacing={1}>
        {keys.map(key => (
          <Grid item xs={12} sm={6} md={4} lg={2} p={0} key={key}>
            <Typography>
              {key} {weights[key]}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  }

  const issuedDate = moment(registrant?.registrationIssueDate, "YYYYMMDD");
  const expirationDate = moment(
    registrant?.registrationExpirationDate,
    "YYYYMMDD",
  );
  const isExpired = moment().isAfter(expirationDate);
  const expiredStyle = isExpired ? { color: "red", fontWeight: "bold" } : {};
  let registrantHeader = "Registrant Information";
  if (isExpired) {
    registrantHeader += " [EXPIRED REGISTRATION]";
  }
  return (
    <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
      <h2 style={{ margin: "10px" }}>Scan Result:</h2>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={6} md={6} lg={6} p={0}>
          <h3 style={{ margin: 0, ...expiredStyle }}>{registrantHeader}</h3>
          <Box
            border={"1px solid black"}
            marginBottom={"10px"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            padding={"10px"}
          >
            <Box display={"flex"} flexDirection={"column"}>
              <span>
                <b>Name and Address</b>
              </span>
              <span>{registrant?.name}</span>
              <span>{registrant?.street}</span>
              <span>
                {registrant?.city} {registrant?.state} {registrant?.zip}
              </span>
            </Box>
            <Box display={"flex"} flexDirection={"column"}>
              <span>
                <b>USDOT:</b> {registrant?.usdot}
              </span>
              <span>
                <b>Carrier:</b> {registrant?.carrier}
              </span>
            </Box>
            <Box display={"flex"} flexDirection={"column"}>
              <span>
                <b>Registration Date: </b>
                {issuedDate.format("DD-MMM-YYYY")}
              </span>
              <span style={expiredStyle}>
                <b>Expiration Date: </b>
                {expirationDate.format("DD-MMM-YYYY")}
              </span>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6} p={0}>
          <h3 style={{ margin: 0 }}>Motor Carrier Information</h3>
          <Box
            border={"1px solid black"}
            marginBottom={"10px"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            padding={"10px"}
          >
            <Box display={"flex"} flexDirection={"column"}>
              <span>
                <b>Name and Address</b>
              </span>
              <span>{motorCarrier?.name}</span>
              <span>{motorCarrier?.street}</span>
              <span>
                {motorCarrier?.city} {motorCarrier?.state} {motorCarrier?.zip}
              </span>
            </Box>
            <Box display={"flex"} flexDirection={"column"}>
              <span>
                <b>Safety USDOT:</b> {motorCarrier?.usdot}
              </span>
            </Box>
            <Box display={"flex"} flexDirection={"column"}></Box>
          </Box>
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6} p={0}>
          <Box display={"flex"} flexDirection={"column"}>
            <h3 style={{ margin: 0 }}>Vehicle Information</h3>
            <Box
              border={"1px solid black"}
              marginBottom={"10px"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              padding={"10px"}
            >
              <Box display={"flex"} flexDirection={"column"}>
                <span>
                  <b>Unit: </b>
                  {vehicle?.unit}
                </span>
                <span>
                  <b>License Plate: </b>
                  {vehicle?.plateNumber}
                </span>
                <span>
                  <b>VIN: </b>
                  {vehicle?.vin}
                </span>
                <span>
                  <b>Vehicle Type: </b>
                  {vehicle?.vehicleType}
                </span>
                <span>
                  <b>Vehicle Make: </b>
                  {vehicle?.vehicleMakeName}
                </span>
                <span>
                  <b>Model Year: </b>
                  {vehicle?.modelYear}
                </span>
              </Box>
              <Box display={"flex"} flexDirection={"column"}>
                <span>
                  <b>Owner: </b>
                  {vehicle?.owner}
                </span>
                <span>
                  <b>Gross Weight: </b>
                  {vehicle?.grossWeight}
                </span>
                <span>
                  <b>Base Weight: </b>
                  {vehicle?.baseWeight}
                </span>
                <span>
                  <b>Axles/Seats: </b>
                  {vehicle?.seatsAxles}
                </span>
              </Box>
              <Box display={"flex"} flexDirection={"column"}></Box>
            </Box>
            <h3 style={{ margin: 0 }}>Make Selection</h3>
            <Box display={"flex"} flexDirection={"row"}>
              <CustomAutoComplete
                autoSelect={false}
                matchFromStart={true}
                title={"Vehicle\xa0Make"}
                options={vehicleMakes}
                addMargin={true}
                value={vehicle?.vehicleMake}
                stateName={"vehicleMake"}
                updateFields={value =>
                  setVehicleObj(prevState => {
                    return { ...prevState, vehicleMake: value };
                  })
                }
                renderOption={option => {
                  if (option.action === "Add") {
                    return option.value;
                  } else {
                    return option.name;
                  }
                }}
                minWidth={"200px"}
                userRoles={[]}
                allowAdd={true}
                canEdit={true}
              />
              <CustomAutoComplete
                autoSelect={false}
                matchFromStart={true}
                title={"Vehicle\xa0Model"}
                options={vehicleModels}
                addMargin={true}
                value={vehicle?.vehicleModel}
                stateName={"vehicleModel"}
                updateFields={value =>
                  setVehicleObj(prevState => {
                    return { ...prevState, vehicleModel: value };
                  })
                }
                renderOption={option => {
                  if (option.action === "Add") {
                    return option.value;
                  } else {
                    return option.name;
                  }
                }}
                minWidth={"200px"}
                userRoles={[]}
                allowAdd={true}
                canEdit={true}
              />
            </Box>
          </Box>
        </Grid>
        {weights && (
          <Grid item xs={6} sm={6} md={6} lg={6} p={0}>
            <>
              <h3 style={{ margin: 0 }}>Weight Information</h3>
              <Box
                border={"1px solid black"}
                marginBottom={"10px"}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                padding={"10px"}
              >
                <Box>
                  (Canadian Provinces are shown in kilograms, all other
                  jurisdictions are shown in pounds.)
                  {renderObjectAsGrid(weights)}
                </Box>
              </Box>
            </>
          </Grid>
        )}
        <Grid item xs={6} sm={6} md={6} lg={6} p={0}>
          {children}
        </Grid>
      </Grid>
    </Box>
  );
};

const DisplayMatches = ({
  vehicleObj,
  user,
  selectedVehicle,
  setSelectedVehicle,
  scanDone,
  onlineState,
  mergeChoice,
  setMergeChoice,
}) => {
  const [
    searchVehiclesByVin,
    { loading: vinSearchLoading, data: vinSearchData },
  ] = useLazyQuery(VEHICLE_SEARCH, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });
  const [
    searchVehiclesByPlate,
    { loading: plateSearchLoading, data: plateSearchData },
  ] = useLazyQuery(VEHICLE_SEARCH, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  useEffect(() => {
    if (vehicleObj && vehicleObj?.plateNumber && onlineState) {
      // const state = states.find(s => s?.name?.startsWith(vehicleObj?.["state"]))
      let searchState = {
        vehicle_attributes: {
          vehicle_plate_name: vehicleObj.plateNumber,
          // vehicle_state_id: state?.id || "",
        },
      };
      searchVehiclesByPlate({
        variables: { perPage: 10, page: 1, json: JSON.stringify(searchState) },
      });
    }

    if (vehicleObj && vehicleObj.vin && onlineState) {
      let searchState = {
        vehicle_attributes: {
          vehicle_vin_name: vehicleObj.vin,
        },
      };
      searchVehiclesByVin({
        variables: { perPage: 10, page: 1, json: JSON.stringify(searchState) },
      });
    }
    // These queries should not be included as GraphQL creates new functions on each call but they don't functionally change between calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleObj, onlineState]);

  const [combinedVehicleSearchResults, setCombinedVehicleSearchResults] =
    useState(null);

  useEffect(() => {
    if (vinSearchData && plateSearchData) {
      const mergedArr = vinSearchData?.searchVehicles.concat(
        plateSearchData?.searchVehicles,
      );
      const uniqueArr = _.uniqBy(mergedArr, "id");
      setCombinedVehicleSearchResults(uniqueArr);
    }
  }, [vinSearchData, plateSearchData]);

  return (
    <div>
      <br />
      {scanDone && !window.navigator.onLine && (
        <p>You are currently offline. Matches cannot be displayed.</p>
      )}
      {scanDone &&
        (!!combinedVehicleSearchResults ||
          vinSearchLoading ||
          plateSearchLoading) && (
          <h3 style={{ margin: "10px" }}>Search Results:</h3>
        )}
      {scanDone && (vinSearchLoading || plateSearchLoading) && (
        <p>Loading matches...</p>
      )}
      {scanDone &&
        combinedVehicleSearchResults &&
        combinedVehicleSearchResults?.length === 0 && <p>No matches found</p>}
      {scanDone &&
        combinedVehicleSearchResults &&
        combinedVehicleSearchResults?.length > 0 &&
        combinedVehicleSearchResults.map((vehicle, index) => {
          return (
            <MatchedVehicle
              key={vehicle?.id + "-" + index + "-nameDob"}
              vehicle={vehicle}
              index={index}
              selectedVehicle={selectedVehicle}
              setSelectedVehicle={setSelectedVehicle}
            />
          );
        })}
      <br />
      {!!vehicleObj && !!selectedVehicle && (
        <Conflicts
          mergeChoice={mergeChoice}
          setMergeChoice={setMergeChoice}
          scanResult={vehicleObj}
          matchResult={selectedVehicle}
        />
      )}
    </div>
  );
};

const MatchedVehicle = ({
  vehicle,
  index,
  selectedVehicle,
  setSelectedVehicle,
}) => {
  return (
    <div
      key={vehicle?.id}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginLeft: "1vw",
      }}
    >
      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedVehicle?.id === vehicle?.id}
              onChange={() =>
                setSelectedVehicle(prevState =>
                  prevState?.id === vehicle.id ? null : vehicle,
                )
              }
              color="primary"
              data-cy={"match-checkbox-" + index}
              inputProps={{ "data-cy": index + "-checkbox-input" }}
            />
          }
        />
      </FormControl>
      <span key={vehicle.id}>
        {vehicle?.vehicleMake && (
          <>
            Make:&nbsp;<b>{vehicle?.vehicleMake?.name}</b>&nbsp;&nbsp;
          </>
        )}
        {vehicle?.vehicleModel && (
          <>
            Model:&nbsp;<b>{vehicle?.vehicleModel?.name}</b>&nbsp;&nbsp;
          </>
        )}
        {vehicle?.year && (
          <>
            Year:&nbsp;<b>{vehicle?.year}</b>&nbsp;&nbsp;
          </>
        )}
        {vehicle?.vehiclePlate && (
          <>
            Plate:&nbsp;<b>{vehicle?.vehiclePlate?.name}</b>&nbsp;&nbsp;
          </>
        )}
        {vehicle?.vehicleState && (
          <>
            State:&nbsp;<b>{vehicle?.vehicleState?.name}</b>&nbsp;&nbsp;
          </>
        )}
        {vehicle?.vehicleVin && (
          <>
            VIN:&nbsp;<b>{vehicle?.vehicleVin?.name}</b>&nbsp;&nbsp;
          </>
        )}
      </span>
    </div>
  );
};

const compareVehicles = (scannedVehicle, match) => {
  return {
    make: {
      scanValue: scannedVehicle?.vehicleMake,
      matchValue: match?.vehicleMake,
      isMatch:
        scannedVehicle?.vehicleMake?.id?.toString() ===
        match?.vehicleMake?.id?.toString(),
    },
    model: {
      scanValue: scannedVehicle?.vehicleModel,
      matchValue: match?.vehicleModel,
      isMatch:
        scannedVehicle?.vehicleModel?.id?.toString() ===
        match?.vehicleModel?.id?.toString(),
    },
    year: {
      scanValue: scannedVehicle?.modelYear,
      matchValue: match?.year,
      isMatch: scannedVehicle?.modelYear === match?.year,
    },
    plate: {
      scanValue: scannedVehicle?.plateNumber,
      matchValue: match?.vehiclePlate,
      isMatch:
        scannedVehicle?.plateNumber?.toUpperCase() ===
        match?.vehiclePlate?.name?.toUpperCase(),
    },
    state: {
      scanValue: scannedVehicle?.state,
      matchValue: match?.vehicleState,
      isMatch:
        scannedVehicle?.state?.toUpperCase() ===
        match?.vehicleState?.name?.toUpperCase(),
    },
    vin: {
      scanValue: scannedVehicle?.vin,
      matchValue: match?.vehicleVin,
      isMatch:
        scannedVehicle?.vin?.toUpperCase() ===
        match?.vehicleVin?.name?.toUpperCase(),
    },
  };
};

const Conflicts = ({
  scanResult,
  matchResult,
  mergeChoice,
  setMergeChoice,
}) => {
  const [comparison, setComparison] = useState();
  const [hasConflict, setHasConflict] = useState(false);

  useEffect(() => {
    let compareResult = compareVehicles(scanResult, matchResult);
    setComparison(compareResult);
    const conflictResult = Object.values(compareResult).some(
      item => !item.isMatch,
    );
    setHasConflict(conflictResult);
  }, [scanResult, matchResult]);

  const handleChoice = (e, property) => {
    let value = e.target.value;
    setMergeChoice(prevState => {
      return { ...prevState, [property]: value };
    });
  };

  if (hasConflict) {
    return (
      <div>
        <br />
        <h3>Conflicts:</h3>
        <p>
          We found differences between the registration scan and your selected
          match. Please use the drop-downs to select the correct values and
            we&apos;ll update the selected vehicle accordingly.
        </p>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
          }}
        >
          {Object.keys(comparison).map(key => {
            if (!comparison[key]?.isMatch) {
              let scanValue = comparison[key]?.scanValue;
              let scanValueName =
                typeof scanValue === "object" ? scanValue?.name : scanValue;
              let matchValue = comparison[key]?.matchValue;
              let matchValueName =
                typeof matchValue === "object" ? matchValue?.name : matchValue;
              return (
                <FormControl key={key}>
                  <InputLabel
                    shrink={true}
                    id={"registration-conflict-input-label-" + key}
                  >
                    {key}
                  </InputLabel>
                  <Select
                    labelId={"registration-conflict-input-label-" + key}
                    id={"registration-conflict-select-" + key}
                    value={
                      mergeChoice[key] ?? comparison[key]?.matchValue ?? ""
                    }
                    onChange={e => handleChoice(e, key)}
                  >
                    <ListSubheader>Scan Result</ListSubheader>
                    {scanValue ? (
                      <MenuItem value={scanValue}>{scanValueName}</MenuItem>
                    ) : (
                      <MenuItem value={""} style={{ fontStyle: "italic" }}>
                        None
                      </MenuItem>
                    )}
                    <ListSubheader>Existing Record</ListSubheader>
                    {matchValue ? (
                      <MenuItem value={matchValue}>{matchValueName}</MenuItem>
                    ) : (
                      <MenuItem value={""} style={{ fontStyle: "italic" }}>
                        None
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              );
            } else {
              return null;
            }
          })}
        </div>
      </div>
    );
  } else {
    return <div>No conflicts</div>;
  }
};
